import { useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, EmptyState, Skeleton, useToast } from '../../ui'
import { api } from '../../lib/api'
import { CONFIG_CHANGED } from '../../lib/config'
import { Pill, errText, when, useAdmin } from './shared'
import styles from './admin.module.css'

interface Override { value: unknown; updated_at: string; updated_by: string | null }
interface Settings {
  ranked_levels_public: { value: boolean; override: Override | null }
  prices_enabled: { value: boolean; env: boolean; override: Override | null }
  promo: { value: { active: boolean; until: string | null }; env: { active: boolean; until: string | null }; override: Override | null }
}
interface AuditRow { id: number; action: string; target: string | null; detail: unknown; created_at: string; admin: string | null }

export function SettingsTab() {
  const toast = useToast()
  const { data: s, reload } = useAdmin<Settings>('/admin/settings')
  const { data: log, reload: reloadLog } = useAdmin<{ data: AuditRow[] }>('/admin/audit')
  const [until, setUntil] = useState('')
  const [busy, setBusy] = useState(false)

  const savePromo = async (body: object, msg: string) => {
    setBusy(true)
    try { await api('/admin/settings/promo', { method: 'PUT', body: JSON.stringify(body) }); toast.show({ tone: 'success', message: msg }); window.dispatchEvent(new Event(CONFIG_CHANGED)); await Promise.all([reload(), reloadLog()]) }
    catch (e) { toast.show({ tone: 'error', message: errText(e) }) } finally { setBusy(false) }
  }
  const setRankedPublic = async (enabled: boolean | null) => {
    let confirm: string | undefined
    if (enabled) {
      confirm = window.prompt('Only after SEBI Research Analyst registration. Every signed-in user will see entry, stop-loss and targets.\n\nType SEBI RA REGISTERED to confirm:') ?? ''
      if (confirm !== 'SEBI RA REGISTERED') { toast.show({ tone: 'error', message: 'Not confirmed — levels stay invite-only' }); return }
    }
    setBusy(true)
    try { await api('/admin/settings/ranked-public', { method: 'PUT', body: JSON.stringify({ enabled, confirm }) }); toast.show({ tone: 'success', message: enabled ? 'Ranked levels now visible to everyone' : 'Ranked levels are invite-only' }); await Promise.all([reload(), reloadLog()]) }
    catch (e) { toast.show({ tone: 'error', message: errText(e) }) } finally { setBusy(false) }
  }
  const untilIso = until ? new Date(`${until}T23:59:59+05:30`).toISOString() : null

  return (
    <div className={styles.section}>
      <div className={styles.grid2}>
        <Card title="Launch offer (all plans free)">
          {!s ? <Skeleton lines={3} label="Loading settings" /> : (
            <div className={styles.switchCard}>
              <div className={styles.switchRow}>
                <Pill tone={s.promo.value.active ? 'ok' : 'off'}>{s.promo.value.active ? 'On' : 'Off'}</Pill>
                <span>{s.promo.value.active ? `Everyone gets the Trader plan free${s.promo.value.until ? ` until ${when(s.promo.value.until)}` : ' (no end date)'}; checkout is closed.` : 'Normal plans and checkout apply.'}</span>
              </div>
              <p className={styles.muted} style={{ margin: 0 }}>{s.promo.override ? `Set here by ${s.promo.override.updated_by ?? 'an admin'} on ${when(s.promo.override.updated_at)} (overrides .env).` : `Following .env (PROMO_ALL_FREE=${String(s.promo.env.active)}).`} Remember the legal pages promise users 7 days’ email notice before the offer ends.</p>
              <div className={styles.toolbar}>
                <label htmlFor="promo-until" className={styles.muted}>End date (IST, optional)</label>
                <input id="promo-until" type="date" className={styles.input} value={until} onChange={e => setUntil(e.target.value)} />
                <Button variant="primary" loading={busy} onClick={() => void savePromo({ active: true, until: untilIso }, 'Launch offer switched on')}>Turn on{until ? ' until date' : ''}</Button>
                <Button loading={busy} onClick={() => { if (window.confirm('End the launch offer now? Users drop to their real plan immediately.')) void savePromo({ active: false }, 'Launch offer switched off') }}>Turn off</Button>
                {s.promo.override && <button type="button" className={styles.linkBtn} onClick={() => void savePromo({ reset: true }, 'Launch offer follows .env again')}>Follow .env</button>}
              </div>
            </div>
          )}
        </Card>
        <Card title="Ranked levels for everyone">
          {!s ? <Skeleton lines={2} label="Loading settings" /> : (
            <div className={styles.switchCard}>
              <div className={styles.switchRow}><Pill tone={s.ranked_levels_public.value ? 'warn' : 'off'}>{s.ranked_levels_public.value ? 'Everyone' : 'Invite-only'}</Pill>
                <span>{s.ranked_levels_public.value ? 'All signed-in users see entry, stop-loss and targets.' : 'Only invited users (Users tab → Full Ranked) and admins see levels; everyone else sees the compliant list.'}</span></div>
              <p className={styles.muted} style={{ margin: 0 }}>Turn on only after SEBI Research Analyst registration.</p>
              <div className={styles.toolbar}>
                {s.ranked_levels_public.value
                  ? <Button loading={busy} onClick={() => void setRankedPublic(null)}>Make invite-only again</Button>
                  : <Button loading={busy} onClick={() => void setRankedPublic(true)}>Show levels to everyone…</Button>}
                <Link to="/admin/ranked">Ranked performance →</Link>
              </div>
            </div>
          )}
        </Card>
        <Card title="Live rates">
          {!s ? <Skeleton lines={2} label="Loading settings" /> : (
            <div className={styles.switchCard}>
              <div className={styles.switchRow}><Pill tone={s.prices_enabled.value ? 'ok' : 'off'}>{s.prices_enabled.value ? 'On' : 'Off'}</Pill><span>{s.prices_enabled.override ? 'Set in the admin console' : `Following .env (PRICES_ENABLED=${String(s.prices_enabled.env)})`}</span></div>
              <p className={styles.muted} style={{ margin: 0 }}>Switch, feed status and the full rate board are on the Live rates tab.</p>
              <Link to="/admin/live">Open Live rates →</Link>
            </div>
          )}
        </Card>
      </div>

      <Card title="Admin activity log" variant="flush">
        {!log ? <div style={{ padding: 16 }}><Skeleton lines={3} label="Loading log" /></div> : log.data.length === 0 ? <EmptyState title="No admin actions yet" compact /> : (
          <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Admin log (scrolls sideways)">
            <table className={styles.table}><caption className="sr-only">Admin activity</caption>
              <thead><tr><th scope="col">When</th><th scope="col">Admin</th><th scope="col">Action</th><th scope="col">Target</th><th scope="col">Detail</th></tr></thead>
              <tbody>{log.data.map(r => <tr key={r.id}><td className={styles.muted}>{when(r.created_at)}</td><td>{r.admin ?? '—'}</td><td><code>{r.action}</code></td><td>{r.target ?? '—'}</td><td className={`${styles.wrap} ${styles.muted}`}>{r.detail ? JSON.stringify(r.detail) : '—'}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
